/**
 * InsightCardDetail — 인사이트 카드 상세 오버레이
 *
 * PostDetailOverlay 패턴 참고.
 * 슬라이드인 패널로 카드 내용 + 속성 편집 + 댓글 표시.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  ArrowLeft, Calendar, Check, ChevronDown,
  ExternalLink, Loader2, MessageCircle, Pencil, Trash2,
  User, AlertTriangle, CheckCircle, Eye, EyeOff, X, Tag,
  Paperclip, FileText, FolderOpen, History, Undo2,
} from 'lucide-react';
import {
  Post, Comment as CommentModel, PostAttachment, PostAuthor,
  getInsightMetadata, InsightStatus, TaskStatus,
  INSIGHT_STATUS_CONFIG, INSIGHT_STATUS_LIST, getTaskStatusLabel,
  getDaysUntilDue,
} from '../../models/Board';
import { formatRelativeTime } from '../../lib/relativeTime';
import {
  fetchPost, fetchComments, deletePost, updatePost,
  updateInsightStatus, updateInsightAssignee, updateInsightDueDate,
  toggleInsightWatcher, addInsightWatcher, uploadPostAttachment,
} from '../../services/BoardService';
import { My } from '../../services/My';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { toast } from '../../components/ui/use-toast';
import { useAppUIContext } from '../../context/AppUIContext';
import { Fire } from '../../services/FireClient';
import { MyNotifs } from '../../services/MyNotifs';
import CommentSection from '../board/CommentSection';
import { ChatFilePickerModal } from '../board/ChatFilePickerModal';
import TaskCompleteModal from './TaskCompleteModal';
import { UserAvatar } from '../components/UserAvatar';
import MessageFileItem from '../components/MessageFileItem';
import { FileAttachment } from '../../models/Message';
import MentionTextArea from '../components/MentionTextArea';
import { UtilApp } from '../../services/Utils/UtilApp';

interface TaskCardDetailProps {
  post: Post;
  channelMembers: Array<{ id: string; name: string; photoUrl?: string | null }>;
  /** 채널/방 id — 댓글의 "채팅에서 가져오기" 기능 활성화용 */
  roomId?: string;
  onClose: () => void;
  onRefresh: () => void;
  /** 바깥클릭/ESC 로 닫으면 안 되는 상태(수정 중·내부 모달 열림)를 부모에 알림 */
  onCloseGuardChange?: (guarded: boolean) => void;
  /** @전체 멘션 권한 (코멘트) */
  canMentionAll?: boolean;
}

const TaskCardDetail: React.FC<TaskCardDetailProps> = ({
  post: initialPost,
  channelMembers,
  roomId,
  onClose,
  onRefresh,
  onCloseGuardChange,
  canMentionAll,
}) => {
  const [post, setPost] = useState(initialPost);
  const [comments, setComments] = useState<CommentModel[]>([]);
  const [isClosing, setIsClosing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);

  // 원본 쓰레드 메시지 모달 표시용 — RecordListView 와 동일 패턴
  const { setMessageToShow } = useAppUIContext();

  // 편집 모드
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editPlainText, setEditPlainText] = useState('');
  const [editAttachments, setEditAttachments] = useState<PostAttachment[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // 첨부 업로드 (편집 모드) — 게시판 게시글과 동일 패턴
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = React.useRef<HTMLInputElement>(null);
  const [showChatFilePicker, setShowChatFilePicker] = useState(false);

  const handleAttachmentSelect = useCallback(async (filesList: FileList | null) => {
    if (!filesList || filesList.length === 0 || !My.user?.id) return;
    setUploadingAttachment(true);
    try {
      const uploaded: PostAttachment[] = [];
      for (const f of Array.from(filesList)) {
        const r = await uploadPostAttachment(f, My.user.id, post.board_id);
        uploaded.push({ ...r, source: 'local' });
      }
      setEditAttachments(prev => [...prev, ...uploaded]);
    } catch (err) {
      console.error('[TaskCardDetail] 첨부 업로드 실패:', err);
      toast({ title: t`Attachment upload failed`, variant: 'destructive' });
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }
  }, [post.board_id]);

  const removeEditAttachment = (idx: number) => setEditAttachments(prev => prev.filter((_, i) => i !== idx));

  // 완료 모달 (상태 picker에서 'done' 선택 시 자동 오픈)
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  // 상태/우선순위 피커
  const [showStatusPicker, setShowStatusPicker] = useState(false);

  const { i18n } = useLingui();
  const meta = useMemo(() => getInsightMetadata(post), [post]);
  const isCreator = My.user?.id === meta?.creator?.uid;
  const isMyPost = My.user?.id === post.author.uid;

  // 상태 변경 주체 (히스토리 기록용)
  const currentActor: PostAuthor | undefined = My.user?.id
    ? { uid: My.user.id, name: My.user.name, photoUrl: My.user.photoUrl || null }
    : undefined;

  // 본문 @멘션 후보 (+ @전체 권한 시 최상단)
  const bodyMentionSuggestions = useMemo(() => {
    const base = channelMembers.filter(m => m.id !== My.user?.id).map(m => ({ id: m.id, display: m.name }));
    return canMentionAll ? [{ id: 'everyone', display: t`Everyone` }, ...base] : base;
  }, [channelMembers, canMentionAll]);

  // 반려(검토대기 → 진행중) 사유 입력
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // 바깥클릭/ESC 닫기 가드: 수정 중이거나 내부 모달/확인창이 열려 있으면 오버레이 닫기 금지
  const closeGuarded =
    isEditing || showCompleteModal || showChatFilePicker || showDeleteConfirm || showRejectInput;
  useEffect(() => {
    onCloseGuardChange?.(closeGuarded);
  }, [closeGuarded, onCloseGuardChange]);

  // 로드
  useEffect(() => {
    setPost(initialPost);
    setShowDeleteConfirm(false);
    fetchPost(initialPost.id).then(p => { if (p) setPost(p); });
    fetchComments(initialPost.id).then(setComments);
    // 태스크 카드 열람 → 해당 카드의 미읽음 알림(source='task') 읽음 처리
    // (게시판 PostDetailOverlay 와 동일 패턴 — 알림 클릭이든 직접 열람이든 카드를 실제 열었을 때)
    const unreadNotifs = MyNotifs.Notifications.filter(
      n => n.isNew && n.source === 'task' && n.postId === initialPost.id,
    );
    if (unreadNotifs.length) Fire.clearNotifications(unreadNotifs);
  }, [initialPost.id]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      onRefresh();
    }, 200);
  }, [onClose, onRefresh]);

  /** 상태 변경
   *  - 'done' 선택 시: 즉시 완료 문서 모달 오픈 (status 변경은 모달 저장 시점에서 일어남)
   *    → 사용자가 모달을 취소하면 상태도 변경되지 않음
   *  - 검토대기 → 진행중(반려): 사유 입력 UI 오픈
   *  - 그 외 상태: 즉시 변경 (변경자 정보를 히스토리에 기록)
   */
  const handleStatusChange = async (status: InsightStatus) => {
    setShowStatusPicker(false);
    if (status === 'done') {
      setShowCompleteModal(true);
      return;
    }
    // 검토대기 상태에서 '진행중'으로 되돌리면 = 반려 → 사유 입력
    if (status === 'in_progress' && meta?.status === 'in_review') {
      setRejectNote('');
      setShowRejectInput(true);
      return;
    }
    try {
      const updated = await updateInsightStatus(post.id, post.metadata, status, currentActor);
      setPost(updated);
      toast({ title: t`Status changed to ${getTaskStatusLabel(status)}` });
    } catch (err) {
      toast({ title: t`Failed to change status`, variant: 'destructive' });
    }
  };

  /** 반려 확정 (검토대기 → 진행중, 사유 필수) */
  const handleReject = async () => {
    const note = rejectNote.trim();
    if (!note) return;
    setIsRejecting(true);
    try {
      const updated = await updateInsightStatus(post.id, post.metadata, 'in_progress', currentActor, note);
      setPost(updated);
      setShowRejectInput(false);
      setRejectNote('');
      toast({ title: t`Sent back for revision` });
    } catch (err) {
      toast({ title: t`Failed to change status`, variant: 'destructive' });
    } finally {
      setIsRejecting(false);
    }
  };

  /** 담당자 변경 */
  const handleAssigneeChange = async (uid: string) => {
    try {
      if (!uid) {
        const updated = await updateInsightAssignee(post.id, post.metadata, null);
        setPost(updated);
        toast({ title: t`Assignee removed` });
      } else {
        const member = channelMembers.find(m => m.id === uid);
        if (member) {
          let updated = await updateInsightAssignee(post.id, post.metadata, {
            uid: member.id,
            name: member.name,
            photoUrl: member.photoUrl ?? null,
          });
          // 담당자를 워쳐에 자동 추가
          updated = await addInsightWatcher(updated.id, updated.metadata, member.id);
          setPost(updated);
          toast({ title: t`Assigned to ${member.name}` });
        }
      }
    } catch (err) {
      toast({ title: t`Failed to change assignee`, variant: 'destructive' });
    }
  };

  /** 마감일 변경 */
  const handleDueDateChange = async (value: string) => {
    try {
      const updated = await updateInsightDueDate(post.id, post.metadata, value || null);
      setPost(updated);
      toast({ title: value ? t`Due date set` : t`Due date removed` });
    } catch (err) {
      toast({ title: t`Failed to change due date`, variant: 'destructive' });
    }
  };

  /** 삭제 */
  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await deletePost(post.id);
      onClose();
      onRefresh();
    } catch (err) {
      console.error('[TaskCardDetail] 삭제 실패:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  /** 댓글 추가 후 새로고침 */
  const handleCommentAdded = useCallback(async () => {
    let updated = await fetchPost(post.id);
    if (updated) {
      // 댓글 작성자를 워쳐에 자동 추가
      if (My.user?.id) {
        updated = await addInsightWatcher(updated.id, updated.metadata, My.user.id);
      }
      setPost(updated);
    }
    const cmts = await fetchComments(post.id);
    setComments(cmts);
  }, [post.id]);

  /** 원본 쓰레드 메시지를 모달로 표시 (RecordListView 와 동일 패턴) */
  const handleGoToThread = async () => {
    const threadId = meta?.source_thread_id;
    if (!threadId) {
      toast({ title: t`No source thread linked` });
      return;
    }
    setLoadingThread(true);
    try {
      const message = await Fire.msg.getMessage(threadId);
      if (message) {
        setMessageToShow(message);
      } else {
        toast({ title: t`Original message not found`, variant: 'destructive' });
      }
    } catch (err) {
      console.error('[TaskCardDetail] 원본 메시지 로드 실패:', err);
      toast({ title: t`Failed to load original message`, variant: 'destructive' });
    } finally {
      setLoadingThread(false);
    }
  };

  /** 수정 시작 */
  const handleEditStart = () => {
    setEditTitle(post.title);
    setEditPlainText(post.plain_text || '');
    setEditAttachments(post.attachments ?? []);
    setIsEditing(true);
  };

  /** 수정 취소 */
  const handleEditCancel = () => {
    setIsEditing(false);
  };

  /** 수정 저장 */
  const handleEditSave = async () => {
    if (!editTitle.trim() || isSaving) return;
    setIsSaving(true);
    try {
      const updated = await updatePost(post.id, {
        title: editTitle.trim(),
        plain_text: editPlainText.trim(),
        attachments: editAttachments,
      });
      setPost(updated);
      setIsEditing(false);
      toast({ title: t`Changes saved` });
      onRefresh();
    } catch (err) {
      toast({ title: t`Failed to save`, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  /** TaskCompleteModal 저장 완료 콜백 */
  const handleArchived = (updatedPost: Post) => {
    setPost(updatedPost);
    onRefresh();
  };

  if (!meta) return null;

  const statusConf = INSIGHT_STATUS_CONFIG[meta.status];

  return (
    <div className={cn(
      "flex-1 flex flex-col min-w-0 min-h-0 bg-background transition-opacity duration-200",
      isClosing ? "opacity-0" : "animate-in fade-in duration-200"
    )}>

        {/* ── Header ── prototype §2 방어적 min-w-0 + 모바일 h=52 / px=10 / gap=1 */}
        <header className="flex items-center gap-1 px-2.5 h-[52px] shrink-0 min-w-0 border-b border-border sm:gap-2 sm:px-[18px] sm:h-14">
          <Button variant="ghost" size="icon" onClick={handleClose} className="size-8">
            <ArrowLeft className="size-4" />
          </Button>
          <span className="text-body font-medium text-[color:rgb(var(--text-body))]"><Trans>Task</Trans></span>
          <div className="flex-1" />

          {/* 수정/삭제 버튼 또는 수정 모드 액션 */}
          {isEditing ? (
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="sm" onClick={handleEditCancel} disabled={isSaving}>
                <X className="size-3.5 mr-1" />
                <Trans>Cancel</Trans>
              </Button>
              <Button size="sm" onClick={handleEditSave} disabled={isSaving || !editTitle.trim()}>
                <Check className="size-3.5 mr-1" />
                {isSaving ? <Loader2 className="size-3 animate-spin mr-1" /> : null}
                <Trans>Save</Trans>
              </Button>
            </div>
          ) : (
            (isCreator || isMyPost) && (
              <div className="flex items-center gap-1 sm:gap-1.5">
                <button
                  onClick={handleEditStart}
                  className="flex items-center justify-center gap-0 h-7 w-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer sm:h-8 sm:w-auto sm:px-2.5 sm:gap-1.5 sm:text-label"
                >
                  <Pencil className="size-3.5" />
                  <span className="hidden sm:inline"><Trans>Edit</Trans></span>
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center justify-center gap-0 h-7 w-8 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer sm:h-8 sm:w-auto sm:px-2.5 sm:gap-1.5 sm:text-label"
                >
                  <Trash2 className="size-3.5" />
                  <span className="hidden sm:inline"><Trans>Delete</Trans></span>
                </button>
              </div>
            )
          )}
        </header>

        {/* ── 삭제 확인 ── */}
        {showDeleteConfirm && (
          <div className="flex items-center gap-3 px-4 py-3 bg-destructive/8 border-b border-destructive/20 animate-in fade-in duration-150">
            <AlertTriangle className="size-4 text-destructive shrink-0" />
            <span className="text-sm text-destructive flex-1">
              <Trans>Delete this insight card? This cannot be undone.</Trans>
            </span>
            <div className="flex gap-1.5 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
                <Trans>Cancel</Trans>
              </Button>
              <Button variant="destructive" size="sm" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? <Loader2 className="size-3 animate-spin mr-1" /> : null}
                <Trans>Delete</Trans>
              </Button>
            </div>
          </div>
        )}

        {/* ── Body ── 모바일 인셋 14px 통일 (list-card 와 동일), desktop 28px */}
        <div className="flex-1 overflow-y-auto min-w-0 px-3.5 pt-4 pb-6 sm:px-7 sm:pt-6 sm:pb-8">
          <article className="max-w-5xl mx-auto">

            {/* 작성자 + 날짜 — 모바일: 12px chip / padding 3 9 3 4 / wrap, desktop: 14px chip */}
            <div className="flex items-center gap-1.5 flex-wrap text-meta text-muted-foreground sm:gap-2.5 sm:text-body">
              <span className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-[3px] rounded-full bg-primary/10 sm:gap-2 sm:pl-1.5 sm:pr-3 sm:py-1">
                <UserAvatar
                  src={post.author.photoUrl ?? null}
                  name={post.author.name}
                  className="size-5"
                />
                <span className="text-meta font-medium text-foreground sm:text-body">{post.author.name}</span>
              </span>
              <span className="text-muted-foreground/50">·</span>
              <time>
                {new Date(post.created_at).toLocaleDateString('ko-KR', {
                  year: 'numeric', month: 'long', day: 'numeric',
                })}
                {' '}
                {new Date(post.created_at).toLocaleTimeString('ko-KR', {
                  hour: '2-digit', minute: '2-digit',
                })}
              </time>
            </div>

            {/* 제목 */}
            {isEditing ? (
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full text-display-md font-bold text-foreground leading-[1.3] tracking-[-0.005em] mt-3 bg-transparent border-b-2 border-primary outline-none pb-1 sm:text-task-title sm:mt-[18px]"
                autoFocus
              />
            ) : (
              <h1 className="text-display-md font-bold text-foreground leading-[1.3] tracking-[-0.005em] mt-3 sm:text-task-title sm:mt-[18px]">
                {post.title}
              </h1>
            )}

            {/* ── 태스크 속성 패널 ──
                패널 박스는 w-full, 내부 입력 박스(상태/담당자/마감일)만 iPhone Pro Max 폭(430px)으로 제한
                viewport(md:)가 아닌 컨테이너 폭 기반(useResponsive override)으로 컬럼 수 결정 */}
            <div className="grid w-full grid-cols-[1fr] gap-y-2.5 p-3.5 bg-muted rounded-xl mt-4 mb-4 sm:grid-cols-[max-content_1fr] sm:gap-x-5 sm:gap-y-[14px] sm:p-5 sm:mt-6 sm:mb-6">
              {/* 상태 — 라벨 */}
              <span className="text-body text-muted-foreground flex items-center gap-2 whitespace-nowrap">
                <CheckCircle className="size-[15px]" />
                <Trans>Status</Trans>
              </span>
              <div className="relative w-full max-w-[430px] min-w-0">
                <button
                  onClick={() => setShowStatusPicker(!showStatusPicker)}
                  className={cn(
                    'w-full flex items-center justify-between gap-1.5 h-9 px-2.5 rounded-md text-body font-semibold transition-colors cursor-pointer border-0',
                    statusConf.bgColor,
                    statusConf.color,
                  )}
                >
                  <span>{getTaskStatusLabel(meta.status)}</span>
                  <ChevronDown className="size-[15px]" />
                </button>
                {showStatusPicker && (
                  <div className="absolute left-0 top-full mt-1 w-36 rounded-xl border border-border bg-background shadow-lg z-50 py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                    {INSIGHT_STATUS_LIST.map(s => {
                      const cfg = INSIGHT_STATUS_CONFIG[s];
                      return (
                        <button
                          key={s}
                          onClick={() => handleStatusChange(s)}
                          className={cn(
                            'w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors cursor-pointer',
                            meta.status === s ? 'bg-muted font-semibold' : 'hover:bg-muted/60',
                          )}
                        >
                          <span className={cfg.color}>{getTaskStatusLabel(s)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 반려 사유 입력 (검토대기 → 진행중) */}
              {showRejectInput && (
                <div className="sm:col-span-2 flex flex-col gap-2 p-3 rounded-lg bg-badge-caution-bg border border-badge-caution/25 animate-in fade-in duration-150">
                  <span className="text-label font-medium text-badge-caution flex items-center gap-1.5">
                    <Undo2 className="size-3.5" />
                    <Trans>Send back for revision — reason required</Trans>
                  </span>
                  <textarea
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    placeholder={t`e.g. Please revise the section on ...`}
                    rows={2}
                    autoFocus
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-body text-foreground outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowRejectInput(false); setRejectNote(''); }}
                      disabled={isRejecting}
                    >
                      <Trans>Cancel</Trans>
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleReject}
                      disabled={isRejecting || !rejectNote.trim()}
                    >
                      {isRejecting ? <Loader2 className="size-3 animate-spin mr-1" /> : null}
                      <Trans>Send back</Trans>
                    </Button>
                  </div>
                </div>
              )}

              {/* 담당자 */}
              <span className="text-body text-muted-foreground flex items-center gap-2 whitespace-nowrap">
                <User className="size-[15px]" />
                <Trans>Assignee</Trans>
              </span>
              <div className="relative w-full max-w-[430px] min-w-0">
                <select
                  value={meta.assignee?.uid ?? ''}
                  onChange={(e) => handleAssigneeChange(e.target.value)}
                  className="w-full h-9 rounded-md border border-border bg-background pl-3 pr-8 text-body text-foreground outline-none focus:ring-2 focus:ring-ring appearance-none cursor-pointer"
                >
                  <option value="">{t`Unassigned`}</option>
                  {channelMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 size-[15px] text-muted-foreground pointer-events-none" />
              </div>

              {/* 마감일 */}
              <span className="text-body text-muted-foreground flex items-center gap-2 whitespace-nowrap">
                <Calendar className="size-[15px]" />
                <Trans>Due Date</Trans>
              </span>
              <div className="flex items-center gap-1.5 w-full max-w-[430px] min-w-0">
                {meta.due_date && (() => {
                  const days = getDaysUntilDue(meta.due_date);
                  const isOverdue = days < 0;
                  const isUrgent = days >= 0 && days <= 2;
                  return (
                    <span className={cn(
                      'text-meta-sm font-semibold px-2 py-[3px] rounded tabular-nums shrink-0',
                      isOverdue ? 'text-destructive bg-destructive/10'
                        : isUrgent ? 'text-badge-warn bg-badge-warn-bg'
                        : 'text-muted-foreground bg-muted/80',
                    )}>
                      {isOverdue ? `D+${Math.abs(days)}` : days === 0 ? 'D-Day' : `D-${days}`}
                    </span>
                  );
                })()}
                <input
                  type="date"
                  value={meta.due_date ?? ''}
                  onChange={(e) => handleDueDateChange(e.target.value)}
                  className="h-9 flex-1 min-w-0 rounded-md border border-border bg-background px-3 text-body text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              {/* 원본 쓰레드 — source_thread_id 있을 때만 노출 (직접 생성 태스크는 hide) */}
              {meta.source_thread_id && (
                <>
                  <span className="text-body text-muted-foreground flex items-center gap-2 whitespace-nowrap">
                    <MessageCircle className="size-[15px]" />
                    <Trans>Source Thread</Trans>
                  </span>
                  <div className="flex items-center min-w-0">
                    <button
                      onClick={handleGoToThread}
                      disabled={loadingThread}
                      className="flex items-center gap-1.5 text-body text-primary hover:underline cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {loadingThread
                        ? <Loader2 className="size-[15px] animate-spin" />
                        : <ExternalLink className="size-[15px]" />
                      }
                      <Trans>View source thread</Trans>
                    </button>
                  </div>
                </>
              )}

              {/* 워칭 토글 */}
              <span className="text-body text-muted-foreground flex items-center gap-2 whitespace-nowrap">
                {meta.watchers.includes(My.user?.id ?? '') ? (
                  <Eye className="size-[15px] text-primary" />
                ) : (
                  <EyeOff className="size-[15px]" />
                )}
                <Trans>Watching</Trans>
              </span>
              <div className="flex items-center gap-2 min-w-0">
                {meta.watchers.length > 0 && (
                  <span className="text-body text-foreground tabular-nums font-medium">{meta.watchers.length}<Trans>명</Trans></span>
                )}
                <button
                  onClick={async () => {
                    try {
                      const updated = await toggleInsightWatcher(
                        post.id, post.metadata, My.user?.id ?? '',
                      );
                      setPost(updated);
                    } catch (err) {
                      console.error('[TaskCardDetail] 워칭 토글 실패:', err);
                    }
                  }}
                  className={cn(
                    'text-label font-medium px-3 py-[5px] rounded-md transition-colors cursor-pointer whitespace-nowrap',
                    meta.watchers.includes(My.user?.id ?? '')
                      ? 'text-primary bg-primary/10 hover:bg-primary/15'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/80',
                  )}
                >
                  {meta.watchers.includes(My.user?.id ?? '') ? (
                    <Trans>Unwatch</Trans>
                  ) : (
                    <Trans>Watch</Trans>
                  )}
                </button>
              </div>

              {/* 태그 (있을 때만) */}
              {post.tags && post.tags.length > 0 && (
                <>
                  <span className="text-body text-muted-foreground flex items-center gap-2 whitespace-nowrap">
                    <Tag className="size-[15px]" />
                    <Trans>Tags</Trans>
                  </span>
                  <div className="flex flex-wrap items-center gap-1.5 w-full max-w-[430px] min-w-0">
                    {post.tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-px rounded-full text-meta-sm bg-[var(--primary-alpha)] text-primary font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {/* 요약 (완료된 태스크의 AI 요약 — completion.summary가 있을 때)
                  라벨은 첫 컬럼, 값은 다음 행으로 떨어져 col-span-full(박스 전체 폭) 사용 */}
            </div>

            {/* 본문 — completion.summary (완료된 태스크의 AI 요약) 우선, 없으면 plain_text.
                별도 라벨/박스 없이 본문 영역에 통합 노출 (디자인 일치). 편집 모드에서는 plain_text 편집. */}
            {(() => {
              const bodyText = (!isEditing && meta.status === 'done' && meta.completion?.summary)
                ? meta.completion.summary
                : post.plain_text;
              return (
                <div className="max-w-none text-foreground text-body leading-[1.65] mt-2 sm:text-reading">
                  {isEditing ? (
                    <MentionTextArea
                      text={editPlainText}
                      onChange={setEditPlainText}
                      suggestions={bodyMentionSuggestions}
                      onSubmit={() => {}}
                      placeholder={t`Enter task description...`}
                      minHeight={120}
                      className="border border-border rounded-lg bg-transparent px-3 py-2 focus-within:ring-2 focus-within:ring-ring"
                    />
                  ) : bodyText ? (
                    <div
                      className="whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: UtilApp.getTextAsHTML(bodyText, My.user?.id ?? '') }}
                    />
                  ) : (
                    <span className="text-muted-foreground italic">
                      <Trans>No content</Trans>
                    </span>
                  )}
                </div>
              );
            })()}

            {/* ── 첨부파일 ── 편집 모드: 추가/삭제 UI, 읽기 모드: 다운로드 링크 */}
            {isEditing ? (
              <div className="flex flex-col gap-2 mt-4">
                <span className="text-meta-sm font-semibold text-foreground flex items-center gap-1">
                  <Paperclip className="size-3" />
                  <Trans>Attachments</Trans>
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={uploadingAttachment}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-meta-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-60"
                  >
                    {uploadingAttachment ? <Loader2 className="size-3.5 animate-spin" /> : <Paperclip className="size-3.5" />}
                    <Trans>Attach file</Trans>
                  </button>
                  {roomId && (
                    <button
                      type="button"
                      onClick={() => setShowChatFilePicker(true)}
                      disabled={uploadingAttachment}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-meta-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-60"
                    >
                      <FolderOpen className="size-3.5" />
                      <Trans>Import from chat</Trans>
                    </button>
                  )}
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={e => handleAttachmentSelect(e.target.files)}
                  />
                </div>
                {editAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {editAttachments.map((att, idx) => (
                      <div
                        key={`${att.url}-${idx}`}
                        className="group inline-flex items-center gap-2 bg-muted/40 rounded-lg pl-2 pr-1 py-1.5 text-xs max-w-[280px]"
                      >
                        {att.group === 'image' ? (
                          <img src={att.url} alt={att.filename} className="size-8 rounded object-cover shrink-0" />
                        ) : (
                          <FileText className="size-4 text-muted-foreground shrink-0" />
                        )}
                        <span className="truncate flex-1 min-w-0 text-foreground/90">{att.filename}</span>
                        <button
                          type="button"
                          onClick={() => removeEditAttachment(idx)}
                          className="size-5 rounded hover:bg-destructive/15 text-muted-foreground hover:text-destructive transition-colors flex items-center justify-center"
                          title={t`Remove`}
                        >
                          <X className="size-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              post.attachments && post.attachments.length > 0 && (() => {
                // 채팅 첨부파일과 동일하게 표시 (이미지 라이트박스/비디오 플레이어/문서 뷰어)
                const files = post.attachments.map(
                  att => new FileAttachment(att.filename, att.url, att.sizeByte, att.group),
                );
                return (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {files.map((file, idx) => (
                      <MessageFileItem
                        key={`${file.url}-${idx}`}
                        file={file}
                        files={files}
                        showTitle={false}
                      />
                    ))}
                  </div>
                );
              })()
            )}

            {/* 완료는 상단 속성 패널의 Status 셀렉터에서 '완료' 선택 시 자동 모달이 뜨도록 일원화됨
                (별도 "Mark as Done" 버튼 제거) */}

            {/* 완료 상태 표시 박스 및 별도 완료 문서 섹션은 제거됨
                태그/요약은 상단 속성 패널의 row로 통합되어 표시됨 */}
          </article>

          {/* ── 상태 변경 히스토리 (감사 로그) ── */}
          {meta?.status_history && meta.status_history.length > 0 && (
            <div className="max-w-5xl mx-auto mt-2 mb-6 px-4 sm:px-0">
              <div className="flex items-center gap-2 mb-3 text-muted-foreground">
                <History className="size-4" />
                <span className="text-label font-medium"><Trans>Status history</Trans></span>
              </div>
              <ol className="relative flex flex-col gap-3 pl-1">
                {[...meta.status_history].reverse().map((h, idx) => {
                  const toStatus: TaskStatus = h.to;
                  const toCfg = INSIGHT_STATUS_CONFIG[toStatus];
                  const toLabel = getTaskStatusLabel(toStatus);
                  const fromLabel = h.from ? getTaskStatusLabel(h.from) : null;
                  const byName = h.by?.name ?? t`Unknown`;
                  return (
                    <li key={`${h.at}-${idx}`} className="flex items-start gap-2.5">
                      <span
                        className={cn('mt-1.5 size-2.5 rounded-full shrink-0', toCfg?.bgColor)}
                        aria-hidden
                      >
                        <span className={cn('block size-full rounded-full', toCfg?.color, 'bg-current')} />
                      </span>
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 text-label text-foreground">
                          <UserAvatar src={h.by?.photoUrl ?? null} name={h.by?.name ?? '?'} className="size-4" />
                          <span className="font-medium">{byName}</span>
                          <span className="text-muted-foreground">
                            {fromLabel
                              ? <Trans>changed {fromLabel} → {toLabel}</Trans>
                              : <Trans>created as {toLabel}</Trans>}
                          </span>
                          <span className="text-muted-foreground/50">·</span>
                          <time className="text-meta text-muted-foreground/70">{formatRelativeTime(h.at, i18n)}</time>
                        </div>
                        {h.note && (
                          <p className="text-meta text-muted-foreground border-l-2 border-border pl-2 mt-0.5 whitespace-pre-wrap break-words">
                            {h.note}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* 댓글 섹션 — 태스크는 멘션된 댓글 확인 현황 표시 */}
          <div className="max-w-5xl mx-auto">
            <CommentSection
              postId={post.id}
              comments={comments}
              onCommentAdded={handleCommentAdded}
              mentionMembers={channelMembers
                .filter(m => m.id !== My.user?.id)
                .map(m => ({ id: m.id, name: m.name, profileUrl: m.photoUrl ?? null }))}
              showReadReceipts
              channelId={roomId}
              canMentionAll={canMentionAll}
            />
          </div>
        </div>

      {/* 채팅에서 파일 가져오기 (편집 모드 첨부용) */}
      {roomId && (
        <ChatFilePickerModal
          open={showChatFilePicker}
          channelId={roomId}
          onClose={() => setShowChatFilePicker(false)}
          onPick={(att) => {
            setEditAttachments(prev => [...prev, att]);
            setShowChatFilePicker(false);
          }}
        />
      )}

      {/* 완료 + 기록 저장 모달 */}
      {showCompleteModal && (
        <TaskCompleteModal
          open={showCompleteModal}
          onOpenChange={setShowCompleteModal}
          post={post}
          threadText="" // 원본 쓰레드는 모달 내부에서 비동기 로드
          roomId={meta?.source_room_id ?? ''}
          onCompleted={handleArchived}
        />
      )}
    </div>
  );
};

export default TaskCardDetail;
